/**
 * Pure diff engine for workflow snapshots.
 *
 * Takes a "current" and "proposed" snapshot (both in the API shape with
 * `{nodes, edges}` where each node is `{id, nodeType, configJson, orderIndex}`)
 * and returns a structured description of added/removed/changed nodes, added/
 * removed edges, and field-level changes within changed nodes.
 *
 * Pure TS — no React / DOM / Effect deps. Consumed by apps/web (Flow Designer
 * review UI) and packages/canary-cli (the `proposals diff` command).
 */

import { pickCanonicalConfig } from './node-fields.js';

/** Canonical node shape used by the diff. Matches the API snapshot shape. */
export interface DiffNode {
  id: string;
  nodeType: string;
  orderIndex?: number;
  configJson: Record<string, unknown>;
}

export interface DiffEdge {
  id: string;
  sourceNodeId: string;
  targetNodeId: string;
  edgeType?: string | null;
}

export interface DiffSnapshot {
  nodes: DiffNode[];
  edges: DiffEdge[];
}

export interface FieldChange {
  /** Dotted path within configJson (e.g. "selector" or "actions.0.value"). */
  path: string;
  before: unknown;
  after: unknown;
}

export interface ChangedNodeDiff {
  id: string;
  nodeType: string;
  /** True if nodeType itself changed (rare). */
  typeChanged: boolean;
  beforeType?: string;
  afterType?: string;
  fieldChanges: FieldChange[];
  /** True if orderIndex changed. */
  moved: boolean;
}

export interface SnapshotDiff {
  addedNodes: DiffNode[];
  removedNodes: DiffNode[];
  changedNodes: ChangedNodeDiff[];
  addedEdges: DiffEdge[];
  removedEdges: DiffEdge[];
  /** Count of nodes touched in any way (added + removed + changed). */
  touchedCount: number;
  /** True when both snapshots are structurally identical. */
  isEmpty: boolean;
}

/** Convert an API-shape snapshot (from a proposal) to canonical diff shape. */
export function fromApiSnapshot(snapshot: {
  nodes?: Array<{
    id: string;
    nodeType: string;
    orderIndex?: number;
    configJson?: Record<string, unknown>;
  }>;
  edges?: Array<{
    id: string;
    sourceNodeId: string;
    targetNodeId: string;
    edgeType?: string | null;
  }>;
}): DiffSnapshot {
  return {
    nodes: (snapshot.nodes ?? []).map((n) => ({
      id: n.id,
      nodeType: n.nodeType,
      orderIndex: n.orderIndex,
      configJson: n.configJson ?? {},
    })),
    edges: (snapshot.edges ?? []).map((e) => ({
      id: e.id,
      sourceNodeId: e.sourceNodeId,
      targetNodeId: e.targetNodeId,
      edgeType: e.edgeType ?? null,
    })),
  };
}

/**
 * Diff two configJson objects, producing a flat list of FieldChange entries
 * keyed by dotted path. Arrays are compared element-wise; non-object values
 * are compared by JSON equality.
 */
function diffConfig(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
  basePath = '',
): FieldChange[] {
  const out: FieldChange[] = [];
  const keys = new Set<string>([...Object.keys(before), ...Object.keys(after)]);

  for (const key of keys) {
    const path = basePath ? `${basePath}.${key}` : key;
    const bv = before[key];
    const av = after[key];

    if (isPlainObject(bv) && isPlainObject(av)) {
      out.push(
        ...diffConfig(
          bv as Record<string, unknown>,
          av as Record<string, unknown>,
          path,
        ),
      );
      continue;
    }

    if (!valuesEqual(bv, av)) {
      out.push({ path, before: bv, after: av });
    }
  }

  return out;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return (
    typeof v === 'object' &&
    v !== null &&
    !Array.isArray(v) &&
    Object.getPrototypeOf(v) === Object.prototype
  );
}

function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || a === undefined || b === null || b === undefined) return a === b;
  try {
    return JSON.stringify(sortObjectKeys(a)) === JSON.stringify(sortObjectKeys(b));
  } catch {
    return false;
  }
}

/**
 * Recursively sort object keys so two semantically-identical configs in
 * different key order compare equal. Arrays keep their order (order IS
 * significant for lists).
 */
function sortObjectKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortObjectKeys);
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(value as Record<string, unknown>).sort()) {
      out[k] = sortObjectKeys((value as Record<string, unknown>)[k]);
    }
    return out;
  }
  return value;
}

/** Main entry point — compute a full diff between two snapshots. */
export function diffSnapshots(
  current: DiffSnapshot,
  proposed: DiffSnapshot,
): SnapshotDiff {
  const currentNodeMap = new Map(current.nodes.map((n) => [n.id, n]));
  const proposedNodeMap = new Map(proposed.nodes.map((n) => [n.id, n]));

  const addedNodes: DiffNode[] = [];
  const removedNodes: DiffNode[] = [];
  const changedNodes: ChangedNodeDiff[] = [];

  for (const node of proposed.nodes) {
    const prev = currentNodeMap.get(node.id);
    if (!prev) {
      addedNodes.push(node);
      continue;
    }
    const typeChanged = prev.nodeType !== node.nodeType;
    // Diff only the canonical (editable) fields — the client hydrates UI
    // handlers and preview objects into `data`, which would otherwise show
    // up here as phantom changes.
    const prevCanonical = pickCanonicalConfig(prev.nodeType, prev.configJson);
    const nextCanonical = pickCanonicalConfig(node.nodeType, node.configJson);
    const fieldChanges = diffConfig(prevCanonical, nextCanonical);
    const moved = false;
    if (typeChanged || fieldChanges.length > 0 || moved) {
      changedNodes.push({
        id: node.id,
        nodeType: node.nodeType,
        typeChanged,
        beforeType: typeChanged ? prev.nodeType : undefined,
        afterType: typeChanged ? node.nodeType : undefined,
        fieldChanges,
        moved,
      });
    }
  }

  for (const node of current.nodes) {
    if (!proposedNodeMap.has(node.id)) removedNodes.push(node);
  }

  const currentEdgeMap = new Map(current.edges.map((e) => [edgeKey(e), e]));
  const proposedEdgeMap = new Map(proposed.edges.map((e) => [edgeKey(e), e]));

  const addedEdges: DiffEdge[] = [];
  const removedEdges: DiffEdge[] = [];
  for (const e of proposed.edges) {
    if (!currentEdgeMap.has(edgeKey(e))) addedEdges.push(e);
  }
  for (const e of current.edges) {
    if (!proposedEdgeMap.has(edgeKey(e))) removedEdges.push(e);
  }

  const touchedCount =
    addedNodes.length + removedNodes.length + changedNodes.length;
  const isEmpty =
    touchedCount === 0 && addedEdges.length === 0 && removedEdges.length === 0;

  return {
    addedNodes,
    removedNodes,
    changedNodes,
    addedEdges,
    removedEdges,
    touchedCount,
    isEmpty,
  };
}

function edgeKey(e: DiffEdge): string {
  return `${e.sourceNodeId}→${e.targetNodeId}:${e.edgeType ?? ''}`;
}
